/**
 * Available AppBar style options.
 *
 * @type {string[]}
 */
export const APP_BAR_STYLES = ['white', 'colored', 'transparent']

/**
 * Logo size preset mappings (key to height in px).
 *
 * @type {Record<string, number>}
 */
export const LOGO_SIZES = {
    small: 32,
    medium: 48,
    large: 64,
}

/**
 * Logo size to AppBar vertical padding mappings (key to padding in px).
 *
 * @type {Record<string, number>}
 */
export const LOGO_PADDINGS = {
    medium: 12,
    large: 16,
}

/**
 * Applies AppBar style, logo position, and logo size overrides from store settings.
 * Sets CSS custom properties (--store-logo-position, --store-logo-size) for the
 * AppBar component to consume.
 *
 * @param {import('@mui/material/styles').Theme} theme - The base MUI theme
 * @param {Object|null} storeSettings - Store customization settings
 * @returns {Object} Modified theme options object
 */
export const applyAppBarOverrides = (theme, storeSettings) => {
    const appBarStyle = storeSettings?.webStoreAppBarStyle || 'white'
    const logoPosition = storeSettings?.webStoreLogoPosition
    const logoSize = storeSettings?.webStoreLogoSize

    const rootStyles = {}

    if (appBarStyle === 'white') {
        rootStyles.backgroundColor = '#FFFFFF'
        rootStyles.color = theme.palette.text.primary
    } else if (appBarStyle === 'colored') {
        rootStyles.backgroundColor = theme.palette.primary.main
        rootStyles.color = theme.palette.primary.contrastText
    } else if (appBarStyle === 'transparent') {
        rootStyles.backgroundColor = 'transparent'
        rootStyles.color = theme.palette.text.primary
        rootStyles.boxShadow = 'none'
    }

    if (logoPosition) {
        rootStyles['--store-logo-position'] = logoPosition
    }

    if (logoSize && logoSize in LOGO_SIZES) {
        rootStyles['--store-logo-size'] = `${LOGO_SIZES[logoSize]}px`

        if (logoSize in LOGO_PADDINGS) {
            rootStyles['--store-appbar-padding'] = `${LOGO_PADDINGS[logoSize]}px`
        }
    }

    return {
        ...theme,
        components: {
            ...theme.components,
            MuiAppBar: {
                ...theme.components?.MuiAppBar,
                styleOverrides: {
                    ...theme.components?.MuiAppBar?.styleOverrides,
                    root: {
                        ...theme.components?.MuiAppBar?.styleOverrides?.root,
                        ...rootStyles,
                    },
                },
            },
        },
    }
}
